import { useCallback } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/query-keys';

/** `/inventory/?state=<id>` answers 404 once that state is gone — the next intraday collection deletes the previous intraday state. */
export const isMissingStateError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

/** The default single retry, except for a missing state: retrying the same id can't succeed. */
export const retryUnlessMissingState = (failureCount: number, error: Error): boolean =>
  !isMissingStateError(error) && failureCount < 1;

/**
 * Wraps a by-state inventory fetch: on a missing state, refetch the states list so the
 * caller re-resolves the newest id (a new query key) instead of sitting on a cached,
 * deleted one for the list's 10-minute staleTime.
 */
export const useRefetchStatesOnMissingState = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async <T>(fetch: () => Promise<T>): Promise<T> => {
      try {
        return await fetch();
      } catch (error) {
        if (isMissingStateError(error)) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.states() });
        }
        throw error;
      }
    },
    [queryClient],
  );
};
